import { Box, Typography, Grid, Button, ButtonBase } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Reveal from '../components/Reveal';
import { tokens } from '../theme';

const CLOUDFRONT_URL = process.env.REACT_APP_CLOUDFRONT_URL;

type CategoryCardProps = {
  imageUrl: string;
  label: string;
  onClick: () => void;
  minHeight: { xs: number; md?: number } | number;
};

/**
 * A real button, not a clickable div. These cards are the primary route into the
 * menu, and they were previously unreachable by keyboard.
 */
const CategoryCard = ({ imageUrl, label, onClick, minHeight }: CategoryCardProps) => (
  <ButtonBase
    onClick={onClick}
    focusRipple
    sx={{
      position: 'relative',
      display: 'block',
      textAlign: 'left',
      height: '100%',
      width: '100%',
      minHeight,
      borderRadius: `${tokens.radius.lg}px`,
      overflow: 'hidden',
      transition: `transform ${tokens.duration.press} ${tokens.ease.out}, box-shadow ${tokens.duration.surface} ease`,
      '&:active': { transform: 'scale(0.99)' },

      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': { boxShadow: tokens.shadow.lg },
        '&:hover .card-image': { transform: 'scale(1.05)' },
        '&:hover .card-scrim': { opacity: 1 },
      },
    }}
  >
    <Box
      className="card-image"
      component="img"
      src={imageUrl}
      alt=""
      loading="lazy"
      decoding="async"
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
        transition: `transform 620ms ${tokens.ease.out}`,
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    />

    {/* Guarantees the label's contrast floor whatever the photograph does. */}
    <Box
      className="card-scrim"
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        background:
          'linear-gradient(to top, rgba(28,18,15,0.72) 0%, rgba(28,18,15,0.3) 34%, rgba(28,18,15,0) 62%)',
        opacity: 0.9,
        transition: `opacity ${tokens.duration.surface} ease`,
      }}
    />

    <Typography
      variant="h4"
      component="span"
      sx={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        p: { xs: 2.5, md: 3.5 },
        color: tokens.color.white,
        fontWeight: 600,
        textShadow: '0 1px 16px rgba(28,18,15,0.4)',
      }}
    >
      {label}
    </Typography>
  </ButtonBase>
);

const MenuPreview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const goToMenu = (category?: string) => {
    navigate(category ? `/menu?category=${encodeURIComponent(category)}` : '/menu');
    window.scrollTo({ top: 0 });
  };

  return (
    <Box
      id="menu-preview-section"
      sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 2, md: 5 },
        backgroundColor: tokens.color.white,
      }}
    >
      <Reveal>
        <Box sx={{ maxWidth: '24ch', mb: { xs: 4, md: 6 } }}>
          <Typography variant="h2" component="h2" sx={{ textWrap: 'balance' }}>
            {(t as any)('menuPreview.title')}
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{ mt: 1.5, color: tokens.color.roseInk, fontWeight: 500, textWrap: 'balance' }}
          >
            {(t as any)('menuPreview.titleContinuation')}
          </Typography>
        </Box>
      </Reveal>

      <Grid container spacing={{ xs: 2, md: 2.5 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Reveal distance={20}>
            <CategoryCard
              imageUrl={`${CLOUDFRONT_URL}/public/TokkiCrepa.webp`}
              label={(t as any)('menuPreview.category1')}
              onClick={() => goToMenu('Crepas')}
              minHeight={{ xs: 340, md: 660 }}
            />
          </Reveal>
        </Grid>

        <Grid container size={{ xs: 12, md: 7 }} spacing={{ xs: 2, md: 2.5 }}>
          <Grid size={12}>
            <Reveal delay={60} distance={20}>
              <CategoryCard
                imageUrl={`${CLOUDFRONT_URL}/public/WaffleBowl.webp`}
                label={(t as any)('menuPreview.category2')}
                onClick={() => goToMenu('Waffles')}
                minHeight={{ xs: 300, md: 318 }}
              />
            </Reveal>
          </Grid>
          <Grid size={12}>
            <Reveal delay={120} distance={20}>
              <CategoryCard
                imageUrl={`${CLOUDFRONT_URL}/public/SodaItaliana.webp`}
                label={(t as any)('menuPreview.category3')}
                onClick={() => goToMenu('Bebidas Frias')}
                minHeight={{ xs: 300, md: 318 }}
              />
            </Reveal>
          </Grid>
        </Grid>
      </Grid>

      <Reveal delay={80}>
        <Box sx={{ mt: { xs: 5, md: 7 }, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => goToMenu()}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
          >
            {(t as any)('menuPreview.exploreButtonText')}
          </Button>
        </Box>
      </Reveal>
    </Box>
  );
}

export default MenuPreview;
